import { useEffect, useState } from "react";
import { Card, Select, Text, Title } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useTranslation } from "react-i18next";
import { getTenantSettings, updateTenantSettings } from "../api";

const LANGUAGE_LABELS = {
  de: "German (Deutsch)",
  en: "English",
  fr: "French (Français)",
  es: "Spanish (Español)",
  it: "Italian (Italiano)",
  nl: "Dutch (Nederlands)",
  pt: "Portuguese (Português)",
};

/**
 * FacetFlux-specific tenant-wide settings card, rendered below the shared
 * tenant profile editor. Currently: the default content language that seeds
 * ContentLanguage on newly created/inferred schemas.
 */
export default function ContentSettingsCard() {
  const { t } = useTranslation();
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getTenantSettings().then(setSettings);
  }, []);

  const handleChange = async (value) => {
    setSaving(true);
    try {
      const updated = await updateTenantSettings({
        defaultContentLanguage: value || null,
      });
      if (updated) {
        setSettings(updated);
        notifications.show({
          title: t("settings.changes_saved"),
          message: t("settings.default_content_language"),
          color: "green",
        });
      }
    } finally {
      setSaving(false);
    }
  };

  if (!settings) return null;

  const options = (settings.supportedContentLanguages || []).map((code) => ({
    value: code,
    label: LANGUAGE_LABELS[code] || code,
  }));

  return (
    <Card withBorder mt="md">
      <Title order={4}>{t("settings.content_generation")}</Title>
      <Text size="sm" c="dimmed" mb="sm">
        {t("settings.content_generation_description")}
      </Text>
      <Select
        label={t("settings.default_content_language")}
        description={t("settings.default_content_language_hint")}
        placeholder={t("settings.default_content_language_not_set")}
        data={options}
        value={settings.defaultContentLanguage || null}
        onChange={handleChange}
        disabled={saving}
        clearable
        maw={420}
      />
    </Card>
  );
}
